import { CheckCheck } from "lucide-react";

import type { InAppNotification } from "@/features/notifications/api/inAppNotificationsApi";
import type { PendingCounts } from "@/features/notifications/api/pendingCountsApi";
import { useTenantTimezone } from "@/features/manifest/model/useTenantTimezone";
import { groupInAppByDay } from "@/features/notifications/lib/groupInAppByDay";
import {
  countForPendingAction,
  pendingActionRows,
} from "@/features/notifications/lib/pendingActionItems";
import { InAppNotificationRow } from "@/features/notifications/ui/InAppNotificationRow";
import {
  NotificationPanelTabs,
  type NotificationPanelTab,
} from "@/features/notifications/ui/NotificationPanelTabs";
import { PendingActionCard } from "@/features/notifications/ui/PendingActionCard";

const FEED_LIMIT = 15;

interface NotificationPanelProps {
  tab: NotificationPanelTab;
  onTabChange: (tab: NotificationPanelTab) => void;
  pending: PendingCounts | undefined;
  inApp: InAppNotification[];
  unread: number;
  onMarkAllRead: () => void;
  onClose: () => void;
  onOpenRow: (id: number, readAt: string | null) => void;
}

export function NotificationPanel({
  tab,
  onTabChange,
  pending,
  inApp,
  unread,
  onMarkAllRead,
  onClose,
  onOpenRow,
}: NotificationPanelProps) {
  const timeZone = useTenantTimezone();
  const needsActionTotal = pending?.total ?? 0;
  const actionRows = pendingActionRows(pending);

  const feedSource =
    tab === "unread" ? inApp.filter((n) => !n.read_at) : inApp.slice(0, FEED_LIMIT);
  const grouped = groupInAppByDay(feedSource, timeZone);

  const showNeedsSection = tab !== "unread" && actionRows.length > 0;
  const showFeed = tab !== "needs_action";

  return (
    <div
      className="absolute right-0 top-full z-50 mt-2 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/80 px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
        {unread > 0 ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-2xs font-medium text-muted-foreground transition-colors hover:text-primary"
            onClick={onMarkAllRead}
          >
            <CheckCheck className="size-3.5" aria-hidden />
            Mark all read
          </button>
        ) : null}
      </div>

      <NotificationPanelTabs
        active={tab}
        onChange={onTabChange}
        needsActionTotal={needsActionTotal}
      />

      <div className="max-h-[min(28rem,70vh)] overflow-y-auto overscroll-contain">
        {tab === "needs_action" && actionRows.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">
            Nothing needs your action right now.
          </p>
        ) : null}

        {showNeedsSection ? (
          <section className="px-2 pb-2" aria-label="Needs action">
            <p className="px-2 pb-1 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
              Needs action
            </p>
            <div className="flex flex-col gap-0.5">
              {actionRows.map((item) => (
                <PendingActionCard
                  key={item.key}
                  title={item.title}
                  subtitle={item.subtitle}
                  count={countForPendingAction(pending, item.key)}
                  to={item.to}
                  icon={item.icon}
                  onNavigate={onClose}
                />
              ))}
            </div>
          </section>
        ) : null}

        {showFeed ? (
          <>
            {grouped.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                {tab === "unread" ? "You're all caught up." : "No recent messages."}
              </p>
            ) : (
              grouped.map((group) => (
                <section key={group.label} aria-label={group.label}>
                  <p className="sticky top-0 z-10 bg-card/95 px-4 py-1.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                    {group.label}
                  </p>
                  {group.items.map((n) => (
                    <InAppNotificationRow
                      key={n.id}
                      notification={n}
                      onOpen={() => {
                        onOpenRow(n.id, n.read_at);
                        onClose();
                      }}
                    />
                  ))}
                </section>
              ))
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
